import React, { ComponentType, HTMLAttributes, useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import { styled } from '@mui/material';
import Revit3DViewControl from './Revit3DViewControl';
import { HintedIcon } from '@/components';
import { FormSubTitle, GroupLabel } from './common.controls';
import CustomCheckbox from './CustomCheckbox';
import t from '@/utils/translate';

type GroupProps = {
  gap?: number;
} & HTMLAttributes<HTMLDivElement>;

const Group: ComponentType<GroupProps> = styled('div')(({ gap }: GroupProps) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: `${gap || 0}px`,
}));

const Comment: ComponentType<HTMLAttributes<HTMLDivElement>> = styled('div')({
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '15px',
  color: '#6B778C',
});

const Form: ComponentType<HTMLAttributes<HTMLFormElement>> = styled('form')({
  display: 'flex',
  margin: 'auto',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: 30,
  width: '100%',
  padding: '20px',
});

type FormDataType = {
  revit3DView: string | null;
  importRevitEdges: boolean;
  importCurves: boolean;
};

type Props = {
  submitTitle: string;
  views: string[];
  onSubmit: (values: FormDataType) => Promise<void>;
  initialValues: {
    revit3DView: string | null;
    importRevitEdges: boolean;
    importCurves: boolean;
  },
};

export default ({
  onSubmit,
  views,
  initialValues,
  submitTitle,
}: Props) => {
  const formik = useFormik({
    initialValues: {
      revit3DView: initialValues.revit3DView || null,
      importRevitEdges: initialValues.importRevitEdges,
      importCurves: initialValues.importCurves,
    },
    onSubmit: async (values, helpers) => {
      if (!values.revit3DView) {
        values.revit3DView = null;
      }
      await onSubmit(values);
      helpers.resetForm({ values });
    },
  });

  useEffect(() => {
    formik.resetForm({
      values: {
        revit3DView: initialValues.revit3DView || null,
        importRevitEdges: initialValues.importRevitEdges,
        importCurves: initialValues.importCurves,
      },
      touched: {
        revit3DView: false,
        importRevitEdges: false,
        importCurves: false,
      },
      errors: {},
    });
  }, [ initialValues ]);

  const onRevit3DViewControlChange = useCallback((v: string | null) => {
    formik.setFieldValue('revit3DView', v)
  }, [ formik ]);

  return (<>
    <Form onSubmit={formik.handleSubmit}>
      <Group gap={10}>
        <FormSubTitle>{t('project.settings.title.import')}</FormSubTitle>
        <Comment>{t('project.hint.settings', true)}</Comment>
      </Group>
      <Group gap={18}>
        <GroupLabel>
          {t('project.settings.label.views')}
          <HintedIcon>{t('project.settings.label.views')}</HintedIcon>
        </GroupLabel>
        
        <Revit3DViewControl
          {...formik.getFieldProps('revit3DView')}
          views={views}
          value={formik.values.revit3DView}
          // disabled={formik.isSubmitting}
          disabled
          error={formik.touched.revit3DView && !!formik.errors.revit3DView}
          helperText={formik.touched.revit3DView && formik.errors.revit3DView}
          onValueChange={onRevit3DViewControlChange}
        />
      </Group>

      <Group gap={18}>
        <GroupLabel>{t('project.label.settings.rendering')}</GroupLabel>
        <CustomCheckbox
          style={{ display: 'block' }}
          label={t('project.settings.label.edges')}
          hint={t('project.settings.label.edges')}
          checked={formik.values.importRevitEdges}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => { formik.setFieldValue('importRevitEdges', event.target.checked) }}
          disabled
        />
      </Group>
    </Form>
  </>);
};
